import { useState } from 'react';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { DEVISE, fmt } from '../../lib/currency';
import { clotureCaisse, libelleTaux, lignesAvoir, quantitesRestantes } from '../../lib/caisse';
import { C } from '../../theme/palette';

/** Raisons de retour proposées ; la dernière laisse la main. */
const MOTIFS = ['Article défectueux', 'Erreur de caisse', 'Client a changé d’avis', 'Autre'];

const REMBOURSEMENTS = [
  ['especes', '💵 Espèces'],
  ['carte', '💳 Carte'],
  ['twint', '📱 TWINT'],
  ['bon', '🎟 Bon d’achat'],
];

const champ = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid ' + C.border,
  borderRadius: 8,
  padding: '9px 10px',
  fontSize: 12,
  background: '#fff',
};

/**
 * Avoir au comptoir : le client rapporte tout ou partie d'un ticket.
 *
 * <p>On choisit ce qui revient, ligne par ligne, dans la limite de ce qui n'a
 * pas déjà été rendu ; la raison et le moyen de remboursement. Le montant rendu
 * suit la quantité, remise comprise : on ne rembourse pas plus que ce qui a été
 * payé.
 *
 * @param {object} props propriétés
 * @param {{ticket: string, lignes: Array, clientId: any, date: string}} props.origine ticket d'origine
 * @param {Array} props.ventes toutes les ventes, pour déduire les avoirs déjà faits
 * @param {number} props.tauxCaisse taux de la société, pour une ligne sans taux propre
 * @param {(lignes: Array, choix: {motif: string, mode: string}) => Promise<void>} props.enregistre
 *        enregistre les lignes d'avoir ; rejette avec un message lisible
 * @param {Function} props.onFermer abandonne
 * @returns {import('react').ReactElement} le formulaire
 */
export function AvoirComptoir({ origine, ventes, tauxCaisse, enregistre, onFermer }) {
  const restantes = quantitesRestantes(origine.lignes, ventes);
  const [retours, setRetours] = useState({});
  const [motif, setMotif] = useState(MOTIFS[0]);
  const [motifLibre, setMotifLibre] = useState('');
  // On rend par le moyen qui a servi à payer : une vente par carte se
  // recréditait en espèces, et la caisse ne tombait plus juste.
  const [mode, setMode] = useState(() => {
    const paye = (origine.lignes || []).map((l) => l.pay || l.paiement).find(Boolean);
    return REMBOURSEMENTS.some(([v]) => v === paye) ? paye : 'especes';
  });
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);

  const motifRetenu = motif === 'Autre' ? motifLibre.trim() : motif;
  let lignes = [];
  let depassement = '';
  try {
    lignes = lignesAvoir({ restantes, retours, motif: motifRetenu, mode, numero: 'apercu' });
  } catch (e) {
    depassement = e.message;
  }
  const totaux = clotureCaisse(lignes, tauxCaisse);
  const rienARendre = restantes.every((r) => r.restante <= 0);

  const fixe = (id, valeur, max) => {
    const brut = String(valeur)
      .replace(',', '.')
      .replace(/[^\d.]/g, '');
    const q = brut === '' ? '' : Math.min(max, +brut || 0);
    setRetours((p) => ({ ...p, [id]: q }));
    setErreur('');
  };

  return (
    <Card style={{ marginBottom: 11, border: '2px solid ' + C.amber }}>
      <Bar
        icon="↩"
        title="Faire un avoir"
        color={C.amber}
        sub={'Ticket ' + (origine.ticket || '—') + ' · ' + origine.date}
      />
      <div style={{ padding: 13 }}>
        {rienARendre && (
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 9 }}>
            Tout ce ticket a déjà été rendu.
          </div>
        )}
        {restantes.map(({ ligne, vendue, rendue, restante }) => (
          <div
            key={ligne.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 0',
              borderBottom: '1px solid ' + C.border,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700 }}>{ligne.nom}</div>
              <div style={{ fontSize: 11, color: C.muted }}>
                {vendue} {ligne.u} vendu(s) · {rendue} rendu(s) · {DEVISE} {fmt(ligne.total)} HT
              </div>
            </div>
            <input
              aria-label={'Quantité rendue ' + ligne.nom}
              inputMode="decimal"
              value={retours[ligne.id] ?? ''}
              disabled={restante <= 0}
              placeholder="0"
              onChange={(e) => fixe(ligne.id, e.target.value, restante)}
              style={{ ...champ, width: 64, textAlign: 'center', fontSize: 14 }}
            />
            <Btn
              label={'Tout (' + restante + ')'}
              sm
              ghost
              color={C.amber}
              disabled={restante <= 0}
              onClick={() => fixe(ligne.id, restante, restante)}
            />
          </div>
        ))}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>Motif</div>
            <select
              aria-label="Motif du retour"
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              style={champ}
            >
              {MOTIFS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>Remboursement</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {REMBOURSEMENTS.map(([k, lb]) => (
                <div
                  key={k}
                  role="button"
                  onClick={() => setMode(k)}
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '8px 4px',
                    borderRadius: 8,
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    border: '1px solid ' + (mode === k ? C.amber : C.border),
                    background: mode === k ? C.amber : '#fff',
                    color: mode === k ? '#fff' : C.grey,
                  }}
                >
                  {lb}
                </div>
              ))}
            </div>
          </div>
        </div>
        {motif === 'Autre' && (
          <input
            aria-label="Précision du motif"
            value={motifLibre}
            onChange={(e) => setMotifLibre(e.target.value)}
            placeholder="Précisez le motif"
            style={{ ...champ, marginTop: 8 }}
          />
        )}
        <div
          style={{
            marginTop: 10,
            background: C.navyXL,
            borderRadius: 9,
            padding: '9px 11px',
            fontSize: 11,
          }}
        >
          {totaux.ventilation.map((v) => (
            <div key={v.taux} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>
                {libelleTaux(v)} sur {fmt(v.ht)}
              </span>
              <span>{fmt(v.tva)}</span>
            </div>
          ))}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: 900,
              fontSize: 12,
              marginTop: 3,
            }}
          >
            <span>À rembourser TTC</span>
            <span style={{ color: C.amber }}>
              {DEVISE} {fmt(Math.abs(totaux.ttc))}
            </span>
          </div>
        </div>
        {(erreur || depassement) && (
          <div
            style={{
              marginTop: 8,
              background: C.redL,
              border: '1px solid ' + C.red,
              borderRadius: 8,
              padding: '8px 10px',
              fontSize: 11,
              color: C.red,
              fontWeight: 700,
            }}
          >
            {erreur || depassement}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <div style={{ flex: 1 }}>
            <Btn
              label={enCours ? 'Enregistrement…' : "✓ Enregistrer l'avoir"}
              color={C.amber}
              full
              disabled={enCours || lignes.length === 0 || !!depassement || !motifRetenu}
              onClick={async () => {
                setEnCours(true);
                setErreur('');
                try {
                  await enregistre(lignesAvoir({ restantes, retours, motif: motifRetenu, mode }), {
                    motif: motifRetenu,
                    mode,
                  });
                } catch (e) {
                  setErreur(e?.message || "L'avoir n'a pas pu être enregistré.");
                } finally {
                  setEnCours(false);
                }
              }}
            />
          </div>
          <Btn label="Annuler" ghost color={C.grey} onClick={onFermer} />
        </div>
      </div>
    </Card>
  );
}
